import { MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UnknownAction } from 'redux';

import { getGlobalSearchResults } from '../../redux/actions/globalSearchActions';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import {
  selectGlobalSearch,
  setCreatedByMeOnly,
  setSearchQuery,
  setShowConversationsOnly,
} from '../../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../../redux/store';
import { AppEnum } from '../../Types/enums';
import DebouncedSearchInput from '../DebouncedSearchInput';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const GlobalSearchInput = ({
  showAdvancedFilters,
  searchSelected,
  setSearchSelected,
  onNavDrawer = false,
}: {
  onNavDrawer?: boolean;
  showAdvancedFilters?: boolean;
  searchSelected: boolean;
  setSearchSelected: (selected: boolean) => UnknownAction;
}) => {
  const { selectedApp } = useSelector(selectAppStatus);
  const analytics = useAnalytics();
  const dispatch = useDispatch<AppDispatch>();
  const { conversationsOnly, createdByMeOnly, searchQuery } = useSelector(selectGlobalSearch);
  const { filter: filterFromURL } = useParams<{ filter?: string }>();

  useEffect(() => {
    if (filterFromURL) {
      if (filterFromURL === 'chats') {
        dispatch(setShowConversationsOnly(true));
      } else if (filterFromURL === 'folders') {
        dispatch(setShowConversationsOnly(false));
      }

      dispatch(setSearchQuery(''));
      dispatch(setCreatedByMeOnly());
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
    }
  }, [filterFromURL, dispatch]);

  const buildSearchAnalytics = useCallback(
    (value: string) => {
      const isNigel = selectedApp === AppEnum.NIGEL;
      const isDealGPT = selectedApp === AppEnum.DEAL_GPT;
      let eventType: AnalyticsEvent | undefined;

      const basePayload = { value };
      const metaPayload: Record<string, boolean | string> = {
        searchQuery: value,
      };

      metaPayload.searchFolders = conversationsOnly !== true;
      metaPayload.searchConversations = conversationsOnly !== false;

      if (isNigel) {
        eventType = onNavDrawer
          ? AnalyticsEvent.WM_NIGEL_LEFT_NAV_SEARCH
          : AnalyticsEvent.WM_NIGEL_ADVANCED_SEARCH;
      } else if (isDealGPT) {
        eventType = onNavDrawer
          ? AnalyticsEvent.WM_IA_LEFT_NAV_SEARCH
          : AnalyticsEvent.WM_IA_ADVANCED_SEARCH;
      }

      if (eventType) {
        analytics.event(eventType, basePayload, metaPayload);
      }
    },
    [selectedApp, onNavDrawer, conversationsOnly, analytics]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '100%',
      }}
    >
      <div
        style={{
          padding: '10px 0px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <DebouncedSearchInput
          value={searchQuery}
          additionalStyles={onNavDrawer ? { backgroundColor: '#E8EEF8' } : {}}
          valueUpdated={(value) => {
            dispatch(setSearchQuery(value));
            dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
            buildSearchAnalytics(value);
          }}
          onFocus={() => {
            dispatch(setSearchSelected(true));
          }}
          onBlur={() => {
            dispatch(setSearchSelected(false));
          }}
          shouldFocus={searchSelected}
          filters={
            showAdvancedFilters
              ? []
              : [
                  {
                    label: 'All',
                    value: conversationsOnly === undefined,
                    onToggle: () => {
                      dispatch(setShowConversationsOnly(undefined));
                      analytics.event(AnalyticsEvent.WM_NIGEL_SEARCH_FILTERING, undefined, {
                        searchFolders: true,
                        searchConversations: true,
                      });
                    },
                  },
                  {
                    label: 'Chats',
                    value: conversationsOnly === true,
                    onToggle: () => {
                      dispatch(setShowConversationsOnly(true));
                      analytics.event(AnalyticsEvent.WM_NIGEL_SEARCH_FILTERING, undefined, {
                        searchFolders: false,
                        searchConversations: true,
                      });
                    },
                  },
                  {
                    label: 'Folders',
                    value: conversationsOnly === false,
                    onToggle: () => {
                      dispatch(setShowConversationsOnly(false));
                      analytics.event(AnalyticsEvent.WM_NIGEL_SEARCH_FILTERING, undefined, {
                        searchFolders: true,
                        searchConversations: false,
                      });
                    },
                  },
                ]
          }
        />
      </div>
      {showAdvancedFilters && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <div
            style={{
              minWidth: '150px',
              marginRight: '16px',
              display: 'flex',
              gap: '8px',
              alignItems: 'baseline',
            }}
          >
            <Typography variant="caption" gutterBottom>
              Creator
            </Typography>
            <Select
              value={
                createdByMeOnly === undefined
                  ? 'all'
                  : createdByMeOnly
                  ? 'createdByMe'
                  : 'createdByOthers'
              }
              onChange={(e) => {
                const val = e.target.value;
                dispatch(
                  setCreatedByMeOnly(
                    val === 'createdByMe' ? true : val === 'createdByOthers' ? false : undefined
                  )
                );
                dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
              }}
              displayEmpty
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="createdByMe">Created By Me</MenuItem>
              <MenuItem value="createdByOthers">Created By Others</MenuItem>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchInput;
